import React from 'react';
import cx from 'classnames';
import { TooltipWithBounds } from '@visx/tooltip';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';

import styles from './SprintTooltip.module.scss';

export { styles as sprintTooltipStyles };

const UNIT_LABELS = {
  tasks: 'Work items',
  story_points: 'Story points',
  hours: 'Hours',
};

export function formatAmount(value, units) {
  if (value == null) {
    return '–';
  }
  return units === 'tasks' ? Math.round(value) : Math.round(value * 10) / 10;
}

/**
 * An amount that changed size during the sprint, e.g. 46 → 50, or a single amount if it didn't.
 * The size the chart doesn't draw (`charted` is 'start' or 'end') is muted, along with the
 * arrow, so the charted size stands out.
 */
export function formatResized(start, end, units, charted = 'start') {
  const from = `${formatAmount(start, units)}`;
  const to = `${formatAmount(end, units)}`;

  if (from === to) {
    return from;
  }

  return (
    <span className={styles.resized}>
      <span className={cx({ [styles.uncharted]: charted !== 'start' })}>
        {from}
      </span>
      <Icon
        icon={faArrowRight}
        className={cx(styles.uncharted, styles['resized-arrow'])}
      />
      <span className={cx({ [styles.uncharted]: charted !== 'end' })}>
        {to}
      </span>
    </span>
  );
}

export function formatPercent(value) {
  return value == null ? '–' : `${Math.round(value * 100)}%`;
}

function formatDate(value) {
  return value
    ? new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
}

function sprintSubtitle({ start_date, end_date, complete_date }, units) {
  const start = formatDate(start_date);
  const end = formatDate(complete_date || end_date);
  const dates = start && end ? `${start} – ${end}` : start;
  const unitLabel = UNIT_LABELS[units] || units;

  return [dates, complete_date === null ? 'Active' : null, unitLabel]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Tooltip for one sprint on the sprint charts.
 * sections: [{ title, headers, columns, rows: [{ label, value | values, marker, total }] }]
 * - headers labels the value columns when a row has several values (e.g. start and end)
 * - columns names what each value column shows ('start' or 'end'), so it gets the chart's
 *   matching background; the end color follows the sprint's state (active or completed)
 * - marker is the className of the matching legend swatch
 * - total styles the row as a sum
 * columns: how many sections sit side by side.
 */
function columnClass(columnKinds, index) {
  const kind = columnKinds?.[index];
  return kind ? styles[`${kind}-column`] : null;
}

function SprintTooltip({ left, top, sprint, units, sections, columns = 1 }) {
  return (
    <TooltipWithBounds
      left={left}
      top={top}
      className={cx(
        styles.tooltip,
        sprint.complete_date === null ? styles.active : styles.completed
      )}
    >
      <div className={styles.title}>{sprint.name}</div>
      <div className={styles.subtitle}>{sprintSubtitle(sprint, units)}</div>
      <div className={styles.sections} style={{ '--columns': columns }}>
        {sections.map(({ title, headers, columns: columnKinds, rows }) => (
          <table key={title} className={styles.section}>
            <thead>
              <tr>
                <th className={styles['section-title']}>{title}</th>
                {headers?.map((header, index) => (
                  <th
                    key={header}
                    className={cx(
                      styles.header,
                      columnClass(columnKinds, index)
                    )}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, value, values = [value], marker, total }) => (
                <tr
                  key={label}
                  className={cx(styles.row, { [styles.total]: total })}
                >
                  <td className={styles.label}>
                    {marker && <span className={cx('legend-marker', marker)} />}
                    {label}
                  </td>
                  {values.map((cellValue, index) => (
                    <td
                      key={index}
                      className={cx(
                        styles.value,
                        columnClass(columnKinds, index)
                      )}
                    >
                      {cellValue}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </TooltipWithBounds>
  );
}

export default SprintTooltip;
